import { useState, useEffect, useCallback } from "react";
import { WANT_STATUSES, normalizeStatus } from "./useWantList";
import { save } from "./stamps";

const LISTS_KEY = "ppt_lists_v1";

// Lists used to be three hardcoded sections, each with its own storage key.
// Anything already saved under those is pulled into the new shape once, so
// upgrading doesn't look like losing your data.
const LEGACY_KEYS = {
  "Want to buy": "ppt_wantlist_v1",
  "Lv.X master set": "lvx_list_v1",
  "One Piece": "op_wantlist_v1",
};

// Lists belong to a game. The two games are kept in separate sections because
// One Piece has no price source at all, so mixing them would put uncosted cards
// next to costed ones.
export const GAMES = { POKEMON: "pokemon", ONE_PIECE: "onepiece" };

// Which game a list that predates this field belongs to.
const gameForName = (name) => (name === "One Piece" ? GAMES.ONE_PIECE : GAMES.POKEMON);

const newId = () => `l${Date.now()}${Math.random().toString(36).slice(2, 6)}`;

function readLegacy(key) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Named lists of cards, created by whoever is using the app.
 *
 * @param seeds { [listName]: card[] } starting cards for a named list, used
 *   only when nothing is saved yet. Deliberately empty in the deployed app:
 *   these lists shipped with one person's own want list in them, so every
 *   visitor arrived to find someone else's cards waiting. A list should start
 *   empty and be filled by whoever owns it.
 */
export default function useLists(seeds = {}) {
  const [lists, setLists] = useState([]);
  // One selection per game, so switching sections doesn't lose your place.
  const [activeIds, setActiveIds] = useState({});
  // Deliberately state, not a ref. A ref flips to true inside the load effect,
  // and the save effect then runs in the *same* commit still holding the
  // initial empty array — so it wrote [] over everything. Under StrictMode the
  // load effect immediately ran a second time, read that [], and rebuilt from
  // seeds: every list the user had made was replaced on refresh. As state, the
  // gate can't open until the render that carries the loaded lists.
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let loaded = null;
    try {
      const raw = window.localStorage.getItem(LISTS_KEY);
      // An empty array is a real answer — the user deleted their last list.
      // Treating it as "nothing saved" resurrected the shipped lists.
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) loaded = parsed;
      }
    } catch {
      /* fall through to building fresh */
    }

    if (!loaded) {
      /* First run on the new shape. Only lists that actually hold something
         are carried over: these names came from one person's own sections, so
         creating them unconditionally meant every visitor opened the app to
         "Lv.X master set" — a set someone else decided to collect. */
      loaded = Object.keys(LEGACY_KEYS)
        .map((name) => ({ name, cards: readLegacy(LEGACY_KEYS[name]) || seeds[name] || [] }))
        .filter((l) => l.cards.length > 0)
        .map((l) => ({ id: newId(), name: l.name, game: gameForName(l.name), cards: l.cards }));

      /* One list, not one per game. Creating both meant opening the app to two
         identical empty "Want to buy" lists, one of which was for a game you
         may never touch — clutter that has to be understood before it can be
         ignored. The other is made when a card from that game needs it. */
      if (!loaded.some((l) => l.game === GAMES.POKEMON)) {
        loaded.push({ id: newId(), name: "Want to buy", game: GAMES.POKEMON, cards: [] });
      }
    }

    // Lists saved before games existed get sorted by name, so nobody's One
    // Piece list turns up under the Pokemon section after an upgrade.
    loaded = loaded.map((l) => (l.game ? l : { ...l, game: gameForName(l.name) }));

    setLists(loaded);
    const firstPerGame = {};
    for (const l of loaded) if (!firstPerGame[l.game]) firstPerGame[l.game] = l.id;
    setActiveIds(firstPerGame);
    setReady(true);
    // seeds are module constants; re-running on identity change would wipe edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!ready) return;
    try {
      save(LISTS_KEY, JSON.stringify(lists));
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.warn("Failed to save lists to localStorage", e);
      }
    }
  }, [lists, ready]);

  const listsFor = useCallback((game) => lists.filter((l) => l.game === game), [lists]);

  const activeFor = useCallback(
    (game) => {
      const mine = lists.filter((l) => l.game === game);
      return mine.find((l) => l.id === activeIds[game]) || mine[0] || null;
    },
    [lists, activeIds]
  );

  const setActive = useCallback((game, id) => {
    setActiveIds((prev) => ({ ...prev, [game]: id }));
  }, []);

  const editCards = useCallback((listId, fn) => {
    setLists((prev) => prev.map((l) => (l.id === listId ? { ...l, cards: fn(l.cards) } : l)));
  }, []);

  const createList = useCallback((name, game = GAMES.POKEMON) => {
    const clean = String(name || "").trim();
    if (!clean) return null;
    const list = { id: newId(), name: clean, game, cards: [] };
    setLists((prev) => [...prev, list]);
    setActiveIds((prev) => ({ ...prev, [game]: list.id }));
    return list.id;
  }, []);

  /* Bringing a list in from somewhere else — another browser, a phone, a
     backup. This is what replaced shipping a list in the bundle: your cards
     travel with you rather than with everyone. */
  const importList = useCallback((name, game = GAMES.POKEMON, cards = []) => {
    const clean = String(name || "").trim() || "Imported list";
    const safe = (Array.isArray(cards) ? cards : [])
      .filter((c) => c && c.id != null)
      .map((c) => {
        // Prices never travel with a list entry, imported or not — a saved
        // number is stale the day after it was saved.
        const { prices, marketPrice, ...rest } = c;
        return { ...rest, id: String(c.id), status: normalizeStatus(c.status), have: c.have === true };
      });
    const list = { id: newId(), name: clean, game, cards: safe };
    setLists((prev) => [...prev, list]);
    setActiveIds((prev) => ({ ...prev, [game]: list.id }));
    return list.id;
  }, []);

  const renameList = useCallback((id, name) => {
    const clean = String(name || "").trim();
    if (!clean) return;
    setLists((prev) => prev.map((l) => (l.id === id ? { ...l, name: clean } : l)));
  }, []);

  const deleteList = useCallback((id) => {
    setLists((prev) => {
      const gone = prev.find((l) => l.id === id);
      const next = prev.filter((l) => l.id !== id);
      if (gone) {
        // Never leave a section with nothing selected — fall back to whatever
        // is left for that same game.
        const fallback = next.find((l) => l.game === gone.game)?.id ?? null;
        setActiveIds((cur) => (cur[gone.game] === id ? { ...cur, [gone.game]: fallback } : cur));
      }
      return next;
    });
  }, []);

  const addCard = useCallback((listId, card, status = "want") => {
    editCards(listId, (cards) => {
      if (cards.some((c) => c.id === card.id)) return cards;
      // Prices deliberately don't travel with a want-list entry; this is about
      // whether you want the card, not what it's worth.
      const { prices, marketPrice, ...rest } = card;
      return [...cards, { ...rest, status, have: false }];
    });
  }, [editCards]);

  /**
   * Add to whichever list this game is using, making one if it has none.
   *
   * Somewhere to put a card has to exist, but it does not have to exist
   * *before* it is wanted. Creating a list per game up front meant opening the
   * app to two identical empty lists; creating one the moment a card needs it
   * costs nothing and leaves the app empty until you put something in it.
   */
  const addCardForGame = useCallback(
    (game, card, status = "want") => {
      const mine = lists.filter((l) => l.game === game);
      const target = mine.find((l) => l.id === activeIds[game]) || mine[0];
      if (target) {
        addCard(target.id, card, status);
        return target.id;
      }
      const { prices, marketPrice, ...rest } = card;
      const made = {
        id: newId(),
        name: "Want to buy",
        game,
        cards: [{ ...rest, status, have: false }],
      };
      setLists((prev) => [...prev, made]);
      setActiveIds((prev) => ({ ...prev, [game]: made.id }));
      return made.id;
    },
    [lists, activeIds, addCard]
  );

  /**
   * Take a card off a list, and hand back what it would take to put it back.
   *
   * Undo has to be exact or it is not undo. addCard appends and resets the
   * status and the have flag, so an undo built on it would return the card to
   * the bottom of the list as a plain "want" — different place, different
   * state, and the person would be right to say it was not what they had.
   *
   * @returns { card, index } or null if there was nothing to remove
   */
  const removeCard = useCallback((listId, cardId) => {
    /* Looked up now, from the lists in hand, rather than inside the updater:
       the updater runs later, during React's next pass, and a value captured
       in it is not there yet when this returns. */
    const cards = lists.find((l) => l.id === listId)?.cards || [];
    const index = cards.findIndex((c) => c.id === cardId);
    if (index < 0) return null;
    editCards(listId, (cs) => cs.filter((c) => c.id !== cardId));
    return { card: cards[index], index };
  }, [lists, editCards]);

  /** Put a card back exactly where and as it was. The other half of removeCard. */
  const restoreCard = useCallback((listId, card, index) => {
    editCards(listId, (cards) => {
      if (cards.some((c) => c.id === card.id)) return cards;   // already back
      const at = Math.min(Math.max(0, index), cards.length);
      return [...cards.slice(0, at), card, ...cards.slice(at)];
    });
  }, [editCards]);

  const cycleStatus = useCallback((listId, cardId) => {
    editCards(listId, (cards) =>
      cards.map((c) => {
        if (c.id !== cardId) return c;
        // Normalised first, so a card still stored as "maybe" advances from
        // want rather than from an index of -1.
        const at = WANT_STATUSES.indexOf(normalizeStatus(c.status));
        return { ...c, status: WANT_STATUSES[(at + 1) % WANT_STATUSES.length] };
      })
    );
  }, [editCards]);

  const toggleHave = useCallback((listId, cardId) => {
    editCards(listId, (cards) => cards.map((c) => (c.id === cardId ? { ...c, have: !c.have } : c)));
  }, [editCards]);

  // Whether a card is already on one of that game's lists. Scoped by game so a
  // One Piece card can't make a Pokemon search result look already-added.
  const isOnAnyList = useCallback(
    (cardId, game = GAMES.POKEMON) =>
      lists.some((l) => l.game === game && l.cards.some((c) => c.id === cardId)),
    [lists]
  );

  return {
    lists,
    listsFor,
    activeFor,
    setActive,
    createList,
    importList,
    renameList,
    deleteList,
    addCard,
    addCardForGame,
    removeCard,
    restoreCard,
    cycleStatus,
    toggleHave,
    isOnAnyList,
  };
}
